import React, { useState, useEffect } from 'react'
import dayjs from 'dayjs'
import { useQuery, useMutation } from '@apollo/client'
import { Button, Row, Col, Popconfirm, message, Select } from 'antd'
import { LoyaltyQrModal } from './styles'
import {
  QUERY_CUSTOMER_CARDS_AND_REWARDS,
  REDEEM_CUSTOMER_REWARD
} from './LoyaltyQueries'
import { FilterIcon, SelectDownArrowIcon } from '@slerp/assets'
import { SelectPrefixWrapper } from '../Widgets'
import groupBy from 'lodash/groupBy'
import { LoyaltyRewardType } from './types'
import styled from '@emotion/styled'

const { Option } = Select

interface CustomerLoyaltyCard {
  id: string
  redeemed: boolean
  redeemed_at: string | null
  loyalty_card: {
    id: string
    name: string
    code: string
  }
  rewards: {
    id: string
    end_datetime: string | null
    redeemed_at: string | null
    reward: {
      id: string
      name: string
    }
  }[]
}

const RewardsRedeemModal = (props: {
  visible: boolean
  onClose: () => void
  customer: any
}) => {
  const { visible, onClose, customer } = props

  const { data, refetch } = useQuery(QUERY_CUSTOMER_CARDS_AND_REWARDS, {
    variables: { customerId: customer?.id },
    skip: !customer?.id
  })

  const isRewardValid = (reward: Partial<LoyaltyRewardType>) => {
    if (reward?.redeemed_at) {
      return false
    }

    if (reward?.end_datetime) {
      return reward?.end_datetime
        ? dayjs.utc().isBefore(dayjs.utc(reward?.end_datetime))
        : true
    }

    return true
  }

  const groupRewards = (
    customer_loyalty: CustomerLoyaltyCard[],
    valid: boolean
  ) => {
    return Object.values(
      groupBy(customer_loyalty, (card) => card?.loyalty_card?.id)
      // @ts-ignore
    ).reduce((acc, customer_cards) => {
      // @ts-ignore
      const rewards = customer_cards?.reduce((acc, card) => {
        return [...acc, ...(card?.rewards || [])]
      }, [])
      const value = {
        loyalty_card: customer_cards[0]?.loyalty_card,
        // @ts-ignore
        rewards: rewards.filter((val: LoyaltyRewardType) =>
          valid ? isRewardValid(val) : !isRewardValid(val)
        )
      }
      return [...acc, value]
    }, [])
  }

  const [showValid, setShowValid] = useState<'valid' | 'invalid'>('valid')

  const [rewardsData, setRewardsData] = useState<any[]>(
    groupRewards(data?.customer_loyalty_cards || [], true)
  )

  useEffect(() => {
    const groupedRewards = groupRewards(
      data?.customer_loyalty_cards || [],
      showValid === 'valid'
    )
    setRewardsData(groupedRewards)
  }, [showValid, data])

  const [redeemCustomerReward] = useMutation(REDEEM_CUSTOMER_REWARD, {
    fetchPolicy: 'no-cache'
  })

  const redeem = (values: {
    customerRewardId: string
    loyaltyCode: string
  }) => {
    message.loading('Redeeming customer reward...')
    redeemCustomerReward({ variables: values })
      .then((result) => {
        message.destroy()
        message.success('Customer reward has been manually redeemed', 2)
        refetch()
      })
      .catch((error) => {
        console.error('Redeem Customer Reward Error: ', error)
        message.destroy()
        message.error(`Unable to redeem customer reward due to ${error}.`, 3)
      })
  }

  const modalFooter = [
    <></>,
    <Button type='ghost' onClick={onClose}>
      Discard
    </Button>
  ]

  const modalTitle = (
    <div className='qr-modal-header'>
      <h2 className='title'>Redeem Rewards</h2>
    </div>
  )

  return (
    <LoyaltyQrModal
      onCancel={onClose}
      open={visible}
      destroyOnClose={true}
      forceRender={true}
      title={modalTitle}
      footer={modalFooter}
    >
      <div className='_mb-8'>
        Customer: <br />
      </div>
      <StyledCustomerName>{`${customer?.first_name} ${customer?.last_name}`}</StyledCustomerName>
      <Row align='middle' className='_mb-24'>
        <Col span={12}>
          <SelectPrefixWrapper
            prefixicon={<FilterIcon />}
            style={{ marginRight: '24px' }}
          >
            <Select
              data-testid='rewards-options'
              suffixIcon={<SelectDownArrowIcon />}
              defaultValue={showValid}
              value={showValid}
              onChange={(value: 'valid' | 'invalid') => {
                setShowValid(value)
              }}
            >
              <Option key='valid' value='valid'>
                Valid Rewards
              </Option>
              <Option key='invalid' value='invalid'>
                Invalid Rewards
              </Option>
            </Select>
          </SelectPrefixWrapper>
        </Col>
      </Row>
      {rewardsData?.length > 0
        ? rewardsData?.some((card) => card?.rewards?.length > 0)
          ? rewardsData?.map((customer_card) => {
              return customer_card?.rewards?.length > 0 ? (
                <CustomerRewardsInfo>
                  <p className='title'>{customer_card?.loyalty_card?.name}</p>
                  {customer_card?.rewards?.map((customer_reward: any) => (
                    <Row align='middle'>
                      <Col span={20}>
                        <span className='name'>
                          {customer_reward?.reward?.name}
                        </span>
                        {customer_reward?.end_datetime && (
                          <p className='datetime'>
                            Expires in:{' '}
                            {dayjs
                              .utc(customer_reward?.end_datetime)
                              .tz('Europe/London')
                              .format('DD/MM/YYYY hh:mm A')}
                          </p>
                        )}
                      </Col>
                      <Col span={4}>
                        {isRewardValid(customer_reward) && (
                          <Popconfirm
                            title='Are you sure you want to manually redeem this reward?'
                            onConfirm={() =>
                              redeem({
                                customerRewardId: customer_reward?.id,
                                loyaltyCode: customer_card?.loyalty_card?.code
                              })
                            }
                            okText='Redeem'
                            cancelText='Cancel'
                          >
                            <Button>Redeem</Button>
                          </Popconfirm>
                        )}
                      </Col>
                    </Row>
                  ))}
                </CustomerRewardsInfo>
              ) : null
            })
          : 'This customer has no rewards to redeem.'
        : 'This customer has no rewards to redeem.'}
    </LoyaltyQrModal>
  )
}

const CustomerRewardsInfo = styled.div`
  > .title {
    color: #d092dd;
    font-weight: bold;
  }

  > * > * > .name {
    color: #4a4a68;
    margin-bottom: 4px;
  }

  > * > * > .datetime {
    color: #8c8ca1;
    margin-bottom: 0;
  }
`

const StyledCustomerName = styled.h3`
  margin-bottom: 32px;

  color: #4a4a68;
  font-size: 16px;
  font-weight: 500;
  text-transform: capitalize;
`

export default RewardsRedeemModal
